import React, {ReactElement, useContext, useEffect, useState} from "react";
import {Button, Form, Input, Modal, Progress, Select, Space, Spin, Table} from "antd";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, DeleteOutlined, EditOutlined, LoadingOutlined} from "@ant-design/icons";
import {ColumnsType} from "antd/es/table";
import FormButtons from "../common/FormButtons";
import {transliterateCyrillicToLatin} from "../../utils/medicalUtils";
import {getAdditionalKeyByTownKey} from "../../i18n/health-area";
import type {StatusResponse} from "../../api-client";
import {
    DentalTreatmentAugmentRequest,
    DentalTreatmentCancelRequest,
    DentalTreatmentOpenRequest,
    Patient,
    ToothTreatment,
    ToothWithRelationships,
    Treatment
} from "../../api-client";
import {getPatientApi, getMedicalStaffApi, getNZIZApi, getTreatmentsApi, getStatusesApi} from "../../api/apiInstances";
import {useParams} from "react-router-dom";
import {TableRowSelection} from "antd/es/table/interface";
import {AlertContext} from "../../context/AlertContext";
import {isMobileOnly} from "react-device-detect";
import moment from "moment";
import {uuid} from "@ant-design/plots/es/utils";
import PatientTreatmentPlanModal from "./PatientTreatmentPlanModal";
import {
    esignAppEndpoint,
    getQualificationCode,
    getToken, validateNzisTreatmentAugmentRequest, validateNzisTreatmentCancelRequest,
    validateNzisTreatmentCreateRequest
} from "../../i18n/nzis-utils";
import SignatureClientErrorModal from "../common/SignatureClientErrorModal";
import {LOCALES} from "../../i18n/locales";
import { formatDate } from "../../i18n/utils";
import logger from "../../utils/logger";
import {resolveHistoricalLabel} from "../../utils/historyUtils";
import {ALERT_DURATIONS} from "../../constants/alerts";

// Local types for NZIS request structures (backend uses generic 'object' in API contract)
interface NzisHeader {
    sender: string;
    senderId: string;
    senderISName: string;
    recipient: string;
    recipientId: string;
    messageId: string;
    messageType: string;
    createdOn: string;
}

interface DentalTreatmentOpenContents {
    dentalTreatment: {
        lrn: string;
        basedOn: string;
        treatmentStart: string;
        treatmentEnd: string;
        adverseConditions: boolean;
        rhifAreaNumber: string | null | undefined;
        medicalStatus: {
            note: string | null | undefined;
        };
        dentalProcedure: object;
    };
    subject: object;
    performer: object;
}

interface DentalTreatmentCancelContents {
    nrnDental: string | null | undefined;
    cancelReason: string;
}

interface DentalTreatmentAugmentContentsRequest {
    dentalTreatment: {
        nrnDental: string | null | undefined;
        basedOn: string | null | undefined;
        treatmentStart: string;
        treatmentEnd: string;
        adverseConditions: boolean;
        rhifAreaNumber: string | null | undefined;
        dentalProcedure: object;
    };
}

interface TableDataType {
    key: string;
    diagnose: ReactElement;
    operation: ReactElement;
    operation_code: ReactElement;
    status: ReactElement;
    tooth: ReactElement;
    date: string;
    actions: ReactElement;
}

interface PatientNzisProps {
    patient: Patient;
}

interface ProcedureMapping {
    key: string;
    description: string;
    cl112: string;
}

const PatientNzis = (props: PatientNzisProps) => {
        const intl = useIntl();
        const [form] = Form.useForm();
        const {setError, setSuccessMessage, setVisible} = useContext(AlertContext);
        const [isSignatureModalOpen, setSignatureModalOpen] = useState<boolean>(false);
        const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
        const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
        const [cancellationReason, setCancellationReason] = useState('');
        const [statuses, setStatuses] = useState<StatusResponse[]>([]);
        const [isTreatmentModalOpen, setIsTreatmentModalOpen] = useState<boolean>(false);
        const [selectedTreatment, setSelectedTreatment] = useState<ToothTreatment | undefined>(undefined);
        const [teethForSelectedAppointment, setTeethForSelectedAppointment] = useState<ToothWithRelationships[]>([]);
        const [nzisStatus, setNzisStatus] = useState<'sent' | 'unsent' | 'all'>('unsent');
        const [date, setDate] = useState<string>("all");
        const [dates, setDates] = useState<string[]>();
        const [treatments, setTreatments] = useState<ToothTreatment[]>([]);
        const [filteredTreatments, setFilteredTreatments] = useState<ToothTreatment[]>([]);
        const [medicalTreatments, setMedicalTreatments] = useState<Map<string, Treatment>>(new Map());
        const [medicalStatuses, setMedicalStatuses] = useState<Map<string, StatusResponse>>(new Map());
        const [loading, setLoading] = useState<boolean>(false);
        const [loadingPercent, setLoadingPercent] = useState<number>(0);
        // Decoupled action states: the PDF download and the NZIS submission must
        // never share loading state (a shared `loading` made the PDF click show
        // the full-page NZIS spinner and disable both buttons).
        const [isPdfDownloading, setIsPdfDownloading] = useState<boolean>(false);
        const [isNzisSubmitting, setIsNzisSubmitting] = useState<boolean>(false);

        const patientApi = getPatientApi();
        const medicalApi = getMedicalStaffApi();
        const nzisApi = getNZIZApi();
        const treatmentsApi = getTreatmentsApi();
        const statusesApi = getStatusesApi();

        const {id} = useParams();

        const loadData = () => {
            patientApi.getPatientToothTreatments(id!)
                .then(res => {
                    setTreatments(res.data);
                    setFilteredTreatments(res.data.filter(item => {
                        if (nzisStatus === 'sent') {
                            return item.isSentToNzis;
                        } else if (nzisStatus === 'unsent') {
                            return !item.isSentToNzis;
                        }

                        return true;
                    }));
                    setDates(res.data
                        .filter(treatment => treatment.date)
                        .sort((treatmentOne, treatmentTwo) => treatmentOne.date!.localeCompare(treatmentTwo.date!))
                        .map(treatment => formatDate(treatment.date!))
                        .filter((value, index, array) => value && array.indexOf(value) === index));
                    statusesApi.fetchAllStatuses()
                        .then(res => {
                            const map = new Map();

                            for (const status of res.data) {
                                map.set(status.id, status);
                            }
                            setMedicalStatuses(map);
                            treatmentsApi.fetchMedicalTreatments()
                                .then(res => {
                                    const map = new Map();

                                    for (const treatment of res.data) {
                                        map.set(treatment.id, treatment);
                                    }
                                    setMedicalTreatments(map);
                                })
                                .catch(err => logger.log(err))
                        })
                        .catch(err => logger.log(err));
                })
                .catch(err => logger.log(err));
        };

        useEffect(() => {
            fetch(esignAppEndpoint, {
                method: 'post',
                headers: {
                    'Accept': 'application/xml',
                    'Content-Type': 'application/xml'
                },
                body: '<h1>Test</h1>'
            })
                .then(res => res.text())
                .then(res => {
                    if (res === 'No certificate is chosen.') {
                        setSignatureModalOpen(true);
                    }
                })
                .catch(() => setSignatureModalOpen(true));
        }, []);


        useEffect(() => {
            statusesApi.fetchAllStatuses()
                .then(res => {
                    setStatuses(res.data);

                    loadData();
                });
        }, []);

        const tableColumns: ColumnsType<TableDataType> = [
            {
                title: useIntl().formatMessage({id: 'date'}),
                dataIndex: 'date',
                key: 'date',
                width: 150,
                sorter: (itemOne, itemTwo) => itemOne.date?.localeCompare(itemTwo.date!)!
            },
            {
                title: useIntl().formatMessage({id: 'diagnose_label'}),
                dataIndex: 'diagnose',
                key: 'diagnose',
                width: 200,
            },
            {
                title: useIntl().formatMessage({id: 'tooth'}),
                dataIndex: 'tooth',
                key: 'tooth',
                width: 200,
            },
            {
                title: useIntl().formatMessage({id: 'operation'}),
                dataIndex: 'operation',
                key: 'operation',
                width: 200,
            },
            {
                title: useIntl().formatMessage({id: 'operation_code'}),
                dataIndex: 'operation_code',
                key: 'operation_code',
                width: 200,
            },
            {
                title: useIntl().formatMessage({id: 'status'}),
                dataIndex: 'status',
                key: 'status',
                width: 200,
            },
            {
                dataIndex: 'actions',
                key: 'actions',
                width: 180,
            },
        ];

        const data: TableDataType[] = filteredTreatments
            .sort((treatmentOne, treatmentTwo) =>
                treatmentOne.date?.localeCompare(treatmentTwo.date!)!)
            .sort((treatmentOne, treatmentTwo) =>
                treatmentTwo.createdAt?.localeCompare(treatmentOne.createdAt!)!)
            .map(treatment => (
                {
                    key: treatment.id!,
                    date: formatDate(treatment.date!),
                    diagnose: <span
                        className="font-weight-700 font-14 font-family-roboto">{treatment.statusId
                        ? resolveHistoricalLabel({ snapshot: treatment.statusNameSnapshot, entity: medicalStatuses.get(treatment.statusId), entityLabelField: 'name' })
                        : <FormattedMessage id="not_available"/>}</span>,
                    tooth: <span>{treatment.tooth?.position}</span>,
                    operation: <span>{resolveHistoricalLabel({ snapshot: treatment.treatmentNameSnapshot, entity: medicalTreatments.get(treatment.treatmentId!), entityLabelField: 'name' })}</span>,
                    operation_code: <span>OPERATION_CODE</span>,
                    status: treatment.isSentToNzis ?
                        <span className="dentobook-green-bg-container"><FormattedMessage id="sent_single"/></span>
                        : <span className="dentobook-red-bg-container"><FormattedMessage id="unsent_single"/></span>,
                    actions: <div>
                        <Button onClick={() => openTreatmentModal(treatment)} style={{width: 20}}
                                className="btn-no-border" ghost={false} size="large">
                            <EditOutlined/>
                        </Button>
                        {' '}
                        <Button style={{width: 20}} className="btn-no-border"
                                ghost={false} size="large" onClick={(event) => {
                            event.stopPropagation();

                            setSelectedTreatment(treatment);
                            setIsConfirmModalOpen(true);
                        }}>
                            <DeleteOutlined/>
                        </Button>
                    </div>
                }));

        const openTreatmentModal = async (selectedTreatment: ToothTreatment) => {
            setSelectedTreatment(selectedTreatment);

            // Fetch full tooth data with relationships for the appointment
            if (selectedTreatment.appointmentId) {
                try {
                    const teethResponse = await patientApi.fetchPatientTeeth(selectedTreatment.appointmentId);
                    setTeethForSelectedAppointment(teethResponse.data);
                } catch (error) {
                    logger.error('Failed to fetch teeth for appointment:', error);
                    setTeethForSelectedAppointment([]);
                }
            } else {
                setTeethForSelectedAppointment([]);
            }

            setIsTreatmentModalOpen(true);
        };

        // Download the printable Ambulatory sheet PDF only.
        //
        // This MUST NOT trigger any NZIS flow (no token fetch, no QES signing,
        // no treatment/medical-notice submission). It requests the PDF as a blob
        // and downloads it client-side. The backend auto-selects the document
        // variant (Bulgarian Ambulatory sheet vs International Visit Summary)
        // based on the organization, so the frontend sends no variant.
        const handleAmbulatorySheet = () => {
            if (process.env.NODE_ENV !== 'production') {
                // Dev trace: proves which path a click takes. The PDF path must
                // NEVER log NZIS_SUBMIT_CLICK / NZIS_API_CALLED_FROM.
                // eslint-disable-next-line no-console
                console.log('AMBULATORY_CLICK');
            }

            if (selectedRowKeys.length === 0) {
                setError(intl.formatMessage({id: 'select_at_least_one_item'}));
                setTimeout(() => {
                    setVisible(false);
                    setError('');
                }, ALERT_DURATIONS.STANDARD);

                return;
            }

            // PDF-only loading state — does NOT touch the shared `loading` used by
            // the NZIS flow, so the full-page NZIS spinner never shows and the
            // "Send to NHIS" button is not disabled by a PDF download.
            setIsPdfDownloading(true);
            // NOTE: the generated client method is typed as returning `void`; passing
            // `responseType: 'blob'` makes axios return the PDF bytes as a Blob.
            // Once the backend spec is regenerated (`npm run api:update`), prefer
            // the typed `getPatientVisitRecord` method instead.
            // `variant` is omitted (auto-resolved server-side) and `debugOverlay`
            // is off; only `toothTreatmentIds` is sent.
            patientApi.generatePdfAmbolatorySheetForPatient(id!, undefined, selectedRowKeys, undefined, {
                responseType: 'blob',
            })
                .then((response) => {
                    setIsPdfDownloading(false);
                    const blob = response.data as unknown as Blob;
                    const url = window.URL.createObjectURL(blob);
                    // Use the server-provided filename (Content-Disposition) so the
                    // name matches the variant the backend actually generated.
                    const disposition = (response.headers?.['content-disposition'] || '') as string;
                    const match = /filename="?([^";]+)"?/.exec(disposition);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = match ? match[1] : 'AmbulatorySheet.pdf';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                })
                .catch(() => {
                    setIsPdfDownloading(false);
                    // PDF generation failure — must NOT reuse the NZIS submission
                    // error message.
                    setError(intl.formatMessage({id: 'ambulatory_sheet_fail'}));
                    setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
                });
        };

        const rowSelection: TableRowSelection<TableDataType> = {
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
                setSelectedRowKeys(newSelectedRowKeys.map(rowSelection => rowSelection.toString()));
            },
        };

        const spinnerIcon = <LoadingOutlined style={{fontSize: 24}} spin/>;


        const handleServerError = (err: any) => {
            logger.log(err);

            setLoading(false);
            setLoadingPercent(0);
            setError(err?.error ?? intl.formatMessage({id: 'nzis_data_fail'}));
            setTimeout(() => {
                setError('');
                setVisible(false);
            }, ALERT_DURATIONS.STANDARD);
        };


        const procedureMappings: ProcedureMapping[] = [
            {key: 'D-01-001', description: 'Обстоен преглед', cl112: '1'},
            {key: 'D-01-002', description: 'Специализиран обстоен преглед', cl112: '1'},
            {key: 'D-01-003', description: 'Обстоен преглед за бременни', cl112: '1'},
            {key: 'D-01-004', description: 'Интраорална инцизия в съединително-тъканни ложи', cl112: '1'},
            {key: 'D-01-005', description: 'Екстраорална инцизия в съединително-тъканни ложи', cl112: '1'},
            {key: 'D-01-006', description: 'Контролен преглед след хирургична манипулация', cl112: '1'},
            {key: 'D-01-007', description: 'Обща анестезия в амбулаторни условия', cl112: '1'},
            {key: 'D-01-008', description: 'Обща анестезия в болнични условия', cl112: '1'},
            {key: 'D-01-009', description: '24-часово активно наблюдение след общата анестезия', cl112: '1'},
            {key: 'D-01-010', description: 'Почистване на зъбен камък', cl112: '1'},
            {key: 'D-02-001', description: 'Обтурация', cl112: '2'},
            {key: 'D-03-001', description: 'Ендодонтско лечение на временен зъб', cl112: '3'},
            {key: 'D-03-002', description: 'Ендодонтско лечение на постоянен зъб', cl112: '3'},
            {key: 'D-03-003', description: 'Отстраняване на коренов щифт', cl112: '3'},
            {key: 'D-04-001', description: 'Екстракция на временен зъб', cl112: '4'},
            {key: 'D-04-002', description: 'Екстракция на постоянен зъб', cl112: '4'},
            {key: 'D-04-003', description: 'Екстракция на дълбоко фрактуриран / дълбоко разрушен зъб', cl112: '4'},
            {key: 'D-04-004', description: 'Екстракция на зъб или ендосален имплант чрез трепанация', cl112: '4'},
            {key: 'D-05-001', description: 'Поставяне на коронка с безпрагова препарационна граница', cl112: '5'},
            {key: 'D-05-002', description: 'Поставяне на коронка с прагова препарационна граница', cl112: '5'},
            {key: 'D-05-003', description: 'Сваляне на коронка', cl112: '5'},
            {key: 'D-06-001', description: 'Поставяне на ендосален имплант', cl112: '6'},
            {key: 'D-06-002', description: 'Поставяне на субпериостален имплант', cl112: '6'},
            {key: 'D-06-003', description: 'Отстраняване на субпериостален имплантат', cl112: '6'},
            {key: 'D-07-001', description: 'Поставяне на член на мостово тяло', cl112: '7'},
            {key: 'D-08-001', description: 'Поставяне на неподвижна шина от композитен материал', cl112: '8'},
            {key: 'D-09-001', description: 'Поставяне на частична протеза', cl112: '9'},
            {key: 'D-09-002', description: 'Поставяне на горна цяла протеза', cl112: '9'},
            {key: 'D-09-003', description: 'Поставяне на долна цяла протеза', cl112: '9'},
            {key: 'D-09-004', description: 'Изработка на тотална горна цяла протеза', cl112: '9'},
            {key: 'D-09-005', description: 'Изработка на тотална долна цяла протеза', cl112: '9'},
        ];

        function findProcedureMappingByDescription(description: string | undefined): ProcedureMapping | undefined {
            let mapping = procedureMappings.find(mapping => mapping.description === description);
            if (!mapping) {
                if (description != null) {
                    mapping = procedureMappings.find(mapping => mapping.description.startsWith(description));
                }
            }

            if (!mapping) {
                if (description != null) {
                    mapping = procedureMappings.find(mapping => mapping.description.includes(description));
                }
            }

            return mapping;
        }

        // The NZIS token/challenge call can fail because NZIS is unreachable —
        // the backend maps that to 502/503/504 (not a crash). Show a friendly red
        // connectivity message in that case; otherwise it's a local QES signature
        // client problem, so show the signature-client modal as before.
        const showTokenError = (err: any) => {
            const httpStatus = err?.response?.status;
            if (httpStatus === 502 || httpStatus === 503 || httpStatus === 504) {
                setError(intl.formatMessage({id: 'nzis_unavailable'}));
                setTimeout(() => {
                    setVisible(false);
                    setError('');
                }, ALERT_DURATIONS.STANDARD);
            } else {
                setSignatureModalOpen(true);
            }
        };

        const handleNZISCreate = async (treatment: ToothTreatment) => {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('NZIS_API_CALLED_FROM', 'handleNZISCreate');
            }
            const today = moment().format('yyyy-MM-DDThh:mm:ss');
            const todayDate = moment().format('yyyy-MM-DD');
            const treatmentDate = moment(treatment?.date).format('yyyy-MM-DDThh:mm:ss');
            const patient = props.patient;
            const patientBirthDate = moment(patient?.birthDate).format('yyyy-MM-DD');

            const status = statuses.find(status => status.id === treatment?.statusId);
            const statusDate = moment(status?.createdAt).format('yyyy-MM-DD');

            const medicalStaff = (await medicalApi.fetchMedicalStaffById(treatment?.medicalStaffId as string)).data;
            const medicalTreatment = (await treatmentsApi.fetchMedicalTreatmentById(treatment.treatmentId as string)).data;
            const procedure = findProcedureMappingByDescription(medicalTreatment.name);
            let qualification: string;

            const missingFields = validateNzisTreatmentCreateRequest(medicalStaff, patient, treatment, intl.locale);
            if (missingFields.length) {
                const fieldsString = missingFields.join(', ');
                const errorMessage = intl.formatMessage({id: 'nzis_mandatory_fields'}).replace('[FIELDS]', fieldsString);
                setError(errorMessage);

                return;
            }

            if (medicalStaff.specialisationCode !== undefined) {
                qualification = getQualificationCode(medicalStaff.specialisationCode);
            } else {
                qualification = '2081';
            }

            const header: NzisHeader = {
                sender: "2",
                senderId: medicalStaff.uin ?? "",
                senderISName: "Dentobook 1.0",
                recipient: "4",
                recipientId: "NHIS",
                messageId: uuid(),
                messageType: "D001",
                createdOn: today,
            };

            const contents: DentalTreatmentOpenContents = {
                dentalTreatment: {
                    lrn: uuid(),
                    basedOn: '',
                    treatmentStart: treatmentDate,
                    treatmentEnd: treatmentDate,
                    adverseConditions: false,
                    rhifAreaNumber: patient.healthRegion,
                    medicalStatus: {
                        note: treatment.notes,
                    },
                    dentalProcedure: {
                        type: procedure?.cl112,
                        sequence: '1',
                        datePerformed: todayDate,
                        financingSource: '4',
                        duration: '30',
                        code: procedure?.key,
                        tooth: treatment.tooth && treatment.tooth.position ? {
                            toothIndex: treatment?.tooth?.position?.toString(),
                            supernumeralIndex: '',
                            dentalStatusDate: statusDate,
                            condition: {
                                code: status?.nzisKey
                            }
                        } : undefined,
                        note: treatment?.notes,
                    },
                },
                subject: {
                    identifierType: '5',
                    identifier: patient.egn,
                    nhifInsuranceNumber: '',
                    birthDate: patientBirthDate,
                    gender: '1',
                    name: {
                        given: patient.firstName ? transliterateCyrillicToLatin(patient.firstName) : patient.firstName,
                        middle: patient.secondName ? transliterateCyrillicToLatin(patient.secondName) : patient.secondName,
                        family: patient.lastName ? transliterateCyrillicToLatin(patient.lastName) : patient.lastName,
                    },
                    address: {
                        country: 'BG',
                        county: getAdditionalKeyByTownKey(patient.healthArea, LOCALES.ENGLISH),
                        ekatte: patient.ekatte,
                        city: patient.town ? transliterateCyrillicToLatin(patient.town) : '',
                        line: patient.addressOne ? transliterateCyrillicToLatin(patient.addressOne) : '',
                        postalCode: '',
                    },
                    nationality: '',
                    phone: patient.phone ?? '',
                    email: patient.email ?? '',
                },
                performer: {
                    pmi: medicalStaff.uin,
                    qualification: qualification ? qualification : '2081',
                    role: '1',
                    practiceNumber: '1232344545',
                    rhifAreaNumber: '',
                    nhifNumber: '',
                    phone: medicalStaff.phone ?? '',
                    email: medicalStaff.email ?? '',
                },
            };

            const dentalTreatmentOpenRequest: DentalTreatmentOpenRequest = {
                header: header,
                contents: contents
            };


            try {
                setLoading(true);
                let token;
                try {
                    token = (await getToken(nzisApi)).data.token;
                } catch (signatureCLientError) {
                    showTokenError(signatureCLientError);
                    setLoading(false);
                    setIsNzisSubmitting(false);
                    setSelectedRowKeys([]);

                    return Promise.reject(new Error('an error occured'));
                }

                const responseXml = (await nzisApi.createNZISedentalTreatmentXml(dentalTreatmentOpenRequest)).data;
                const responseSignedXml = await fetch(esignAppEndpoint, {
                    method: 'post',
                    headers: {
                        'Accept': 'application/xml',
                        'Content-Type': 'application/xml'
                    },
                    body: responseXml
                });
                const signedXml = await responseSignedXml.text();

                const responseOne = await nzisApi.createNZISedentalTreatment({token, data: signedXml});
                setLoading(false);
                const errorReason = responseOne.data.contents?.errorMessage;
                if (!errorReason) {
                    return patientApi.changePatientTreatmentNzisStatus({
                        treatmentId: treatment.id!,
                        sentToNzis: true,
                        nrnDental: responseOne.data.contents?.nrnDental
                    })
                } else {
                    setLoading(false);
                    handleServerError(errorReason ? {error: errorReason} : {});

                    return Promise.reject(new Error('an error occured'));
                }
            } catch (err) {
                logger.log(err);
                setLoading(false);
                handleServerError(err);

                return Promise.reject(new Error('an error occured'));
            }
        }

        const handleNZISAugment = async (treatment: ToothTreatment) => {
            const today = moment().format('yyyy-MM-DDThh:mm:ss');
            const todayDate = moment().format('yyyy-MM-DD');
            const treatmentDate = moment(treatment.date).format('yyyy-MM-DDThh:mm:ss');
            const patient = props.patient;
            const medicalTreatment = (await treatmentsApi.fetchMedicalTreatmentById(treatment.treatmentId as string)).data;
            const medicalStaff = (await medicalApi.fetchMedicalStaffById(treatment?.medicalStaffId as string)).data;
            const procedure = findProcedureMappingByDescription(medicalTreatment.name);
            const status = statuses.find(status => status.id === treatment.statusId);
            const statusDate = moment(status?.createdAt).format('yyyy-MM-DD');

            const header: NzisHeader = {
                sender: "2",
                senderId: medicalStaff.uin ?? "",
                senderISName: "Dentobook 1.0",
                recipient: "4",
                recipientId: "NHIS",
                messageId: uuid(),
                messageType: "D003",
                createdOn: today,
            };

            const contents: DentalTreatmentAugmentContentsRequest = {
                dentalTreatment: {
                    nrnDental: treatment.nrnDental,
                    basedOn: treatment.nrnDental,
                    treatmentStart: treatmentDate,
                    treatmentEnd: treatmentDate,
                    adverseConditions: false,
                    rhifAreaNumber: patient.healthRegion,
                    dentalProcedure: {
                        type: procedure?.cl112,
                        sequence: '1',
                        index: '1',
                        datePerformed: todayDate,
                        financingSource: '4',
                        code: procedure?.key,
                        tooth: treatment.tooth && treatment.tooth.position ? {
                            toothIndex: treatment?.tooth?.position?.toString(),
                            supernumeralIndex: '',
                            dentalStatusDate: statusDate,
                            condition: {
                                code: status?.nzisKey
                            }
                        } : undefined,
                        note: treatment?.notes
                    },
                },
            };

            const missingFields = validateNzisTreatmentAugmentRequest(medicalStaff, patient, treatment, intl.locale);
            if (missingFields.length) {
                const fieldsString = missingFields.join(', ');
                const errorMessage = intl.formatMessage({id: 'nzis_mandatory_fields'}).replace('[FIELDS]', fieldsString);
                setError(errorMessage);

                return;
            }

            const dentalTreatmentAugmentRequest: DentalTreatmentAugmentRequest = {
                header: header,
                contents: contents
            };

            try {
                setLoading(true);
                let token;
                try {
                    token = (await getToken(nzisApi)).data.token;
                } catch (signatureCLientError) {
                    showTokenError(signatureCLientError);
                    setLoading(false);
                    setIsNzisSubmitting(false);
                    setSelectedRowKeys([]);

                    return Promise.reject(new Error('an error occured'));
                }

                const responseXml = (await nzisApi.editNZISedentalTreatmentXml(dentalTreatmentAugmentRequest)).data;
                const responseSignedXml = (await fetch(esignAppEndpoint, {
                    method: 'post',
                    headers: {
                        'Accept': 'application/xml',
                        'Content-Type': 'application/xml'
                    },
                    body: responseXml
                })).text();

                const signedXml = await responseSignedXml;

                const responseOne = await nzisApi.editNZISedentalTreatment({token, data: signedXml});

                logger.log(responseOne);

                const errorReason = responseOne.data.contents?.errorMessage;
                if (!errorReason) {
                    return patientApi.changePatientTreatmentNzisStatus({
                        treatmentId: treatment.id!,
                        sentToNzis: true
                    });
                } else {
                    setLoading(false);
                    handleServerError(errorReason ? {error: errorReason} : {});

                    return Promise.reject(new Error('an error occured'));
                }
            } catch (err) {
                logger.log(err);
                setLoading(false);
                handleServerError(err);

                return Promise.reject(new Error('an error occured'));
            }
        }

        const handleNZISMultipleItems = (index: number = 0) => {
            if (process.env.NODE_ENV !== 'production' && index === 0) {
                // eslint-disable-next-line no-console
                console.log('NZIS_SUBMIT_CLICK');
            }
            if (selectedRowKeys.length === 0) {
                setError(intl.formatMessage({id: 'select_at_least_one_item'}));
                setTimeout(() => {
                    setVisible(false);
                    setError('');
                }, ALERT_DURATIONS.STANDARD);

                return;
            }

            setIsNzisSubmitting(true);
            const loadingPercentIncrement = Math.round(100 / selectedRowKeys.length);

            if (selectedRowKeys.length > 1) {
                let percent = loadingPercentIncrement * (index + 1);
                if (percent === 100) {
                    percent = 99;
                }
                setLoadingPercent(percent);
            } else {
                setLoading(true);
            }
            const treatmentId = selectedRowKeys[index];
            const treatment = treatments.find(treatment => treatment.id === treatmentId)!;

            const successFunction = () => {
                if (index >= selectedRowKeys.length - 1) {
                    setLoadingPercent(0);
                    setLoading(false);
                    setIsNzisSubmitting(false);
                    setSelectedRowKeys([]);
                    loadData();
                    setVisible(true);
                    setSuccessMessage(intl.formatMessage({id: 'nzis_data_success'}));
                    setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);

                    return;
                }
                handleNZISMultipleItems(index + 1);
            };

            if (!treatment.nrnDental) {
                handleNZISCreate(treatment)
                    .then(successFunction)
                    .catch(err => {
                        setIsNzisSubmitting(false);
                        logger.log(err);
                    });
            } else {
                handleNZISAugment(treatment)
                    .then(successFunction)
                    .catch(err => {
                        setIsNzisSubmitting(false);
                        logger.log(err);
                    });
            }
        };

        const handleNZISSingleItem = (treatment: ToothTreatment) => {
            setLoading(true);
            const successFunction = () => {
                setSelectedRowKeys([]);
                setLoading(false);
                loadData();
                setVisible(true);
                setSuccessMessage(intl.formatMessage({id: 'nzis_data_success'}));
                setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            };
            if (!treatment.nrnDental) {
                handleNZISCreate(treatment)
                    .then(successFunction)
                    .catch(err => logger.log(err));
            } else {
                handleNZISAugment(treatment)
                    .then(successFunction)
                    .catch(err => logger.log(err));
            }
        }


        const handleNZISCancel = async () => {
            const today = moment().format('yyyy-MM-DDThh:mm:ss');
            const medicalStaff = (await medicalApi.fetchMedicalStaffById(selectedTreatment?.medicalStaffId as string)).data;

            const header: NzisHeader = {
                sender: "2",
                senderId: medicalStaff.uin ?? "",
                senderISName: "string",
                recipient: "4",
                recipientId: "NHIS",
                messageId: uuid(),
                messageType: "D007",
                createdOn: today,
            };

            const contents: DentalTreatmentCancelContents = {
                nrnDental: selectedTreatment?.nrnDental,
                cancelReason: 'test'
            };

            const dentalTreatmentAugmentRequest: DentalTreatmentCancelRequest = {
                header: header,
                contents: contents
            };

            const missingFields = validateNzisTreatmentCancelRequest(selectedTreatment, intl.locale);
            if (missingFields.length) {
                const fieldsString = missingFields.join(', ');
                const errorMessage = intl.formatMessage({id: 'nzis_mandatory_fields'}).replace('[FIELDS]', fieldsString);
                setError(errorMessage);

                return;
            }

            try {
                setLoading(true);
                let token;
                try {
                    token = (await getToken(nzisApi)).data.token;
                } catch (signatureCLientError) {
                    showTokenError(signatureCLientError);
                    setLoading(false);
                    setIsNzisSubmitting(false);
                    setSelectedRowKeys([]);

                    return Promise.reject(new Error('an error occured'));
                }

                setLoading(true);
                const signResponse = await nzisApi.cancelNZISedentalTreatmentXml(dentalTreatmentAugmentRequest);
                const signedXml = await fetch(esignAppEndpoint, {
                    method: 'post',
                    headers: {
                        'Accept': 'application/xml',
                        'Content-Type': 'application/xml'
                    },
                    body: signResponse.data
                }).then(res => res.text());

                const nzisResponse = await nzisApi.cancelNZISedentalTreatment({token, data: signedXml});
                setLoading(false);
                if (nzisResponse.data) {
                    handleServerError({});
                    return;
                }
            } catch (error) {
                handleServerError(error);
                setLoading(false);
                throw error;
            }
        }


        const handleConfirmOk = async () => {
            if (!selectedTreatment) {
                return;
            }
            try {
                await form.validateFields();
                handleNZISCancel().then(() => {
                    patientApi.deleteToothTreatment(selectedTreatment.appointmentId!, selectedTreatment?.id!)
                        .then(() => {
                            setVisible(true);
                            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
                            loadData();
                        })
                        .catch(err => logger.log(err));
                })
                    .catch(err => logger.log(err));

                form.resetFields();
                setIsConfirmModalOpen(false);
            } catch (errorInfo) {
                logger.log('Failed:', errorInfo);
            }
        };

        const handleConfirmCancel = () => {
            form.resetFields();
            setIsConfirmModalOpen(false);
        };


        return (
            <>
                {loadingPercent ?
                    <div className="dentobook-overlay">
                        <div className="dentobook-progress-bar">
                            <Progress percent={loadingPercent} type="circle" trailColor={"lightgray"} strokeColor={"blue"}/>
                        </div>
                    </div>
                    : null}
                <SignatureClientErrorModal isModalOpen={isSignatureModalOpen} setIsModalOpen={setSignatureModalOpen}/>
                <Modal
                    transitionName=""
                    style={{width: '100%', height: '100%'}}
                    className={'dentobook-modal'}
                    onOk={handleConfirmOk}
                    open={isConfirmModalOpen}
                    onCancel={handleConfirmCancel}
                    footer={null}
                >
                    <div className="dentobook-container">
                        <Form form={form} layout="vertical">
                            <strong><FormattedMessage id="confirmation_text"/></strong>
                            <br/><br/>
                            <Form.Item
                                className="input-ninety-percents dentobook-inputs"
                                label={<FormattedMessage id="cancellation_reason"/>}
                                name="cancellationReason"
                                rules={[{required: true, message: intl.formatMessage({id: 'required_field'})}]}
                            >
                                <Input
                                    className="input-ninety-percents dentobook-inputs"
                                    placeholder={useIntl().formatMessage({id: 'cancellation_reason_placeholder'})}
                                    value={cancellationReason}
                                    onChange={(e) => setCancellationReason(e.target.value)}
                                />
                            </Form.Item>
                        </Form>

                        <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 16}}>
                            <Button
                                className={"dentobook-btn-secondary modal-btn-md font-16 font-weight-500"}
                                onClick={handleConfirmCancel}
                            >
                                {<FormattedMessage id="cancel"/>}
                            </Button>
                            <Button
                                className={"dentobook-btn-primary modal-btn-lg font-16 font-weight-500"}
                                style={{marginLeft: 8, width: "auto"}}
                                onClick={handleConfirmOk}
                            >
                                {isMobileOnly ? <FormattedMessage id="confirm"/> : <FormattedMessage id="nzis_confirm"/>}
                            </Button>
                        </div>
                    </div>
                </Modal>
                <Spin indicator={spinnerIcon} spinning={loading}
                      style={{position: "fixed", top: "50%", left: "50%", zIndex: "99999"}}/>
                {selectedTreatment && (
                    <PatientTreatmentPlanModal appointment={selectedTreatment.appointmentId!}
                                               mode={"edit"}
                                               patient={props.patient}
                                               statuses={Array.from(medicalStatuses.values())}
                                               selectedTreatment={selectedTreatment}
                                               loadData={loadData}
                                               allTeethPositions={teethForSelectedAppointment}
                                               toothObjs={teethForSelectedAppointment.filter(t => t.id === selectedTreatment.toothId)}
                                               isModalOpen={isTreatmentModalOpen}
                                               setIsModalOpen={setIsTreatmentModalOpen}
                                               title={selectedTreatment.tooth?.position?.toString()}
                                               sendDataToNZIS={handleNZISSingleItem}/>
                )}
                <div className="dentobook-container">
                    <Form layout="vertical">
                        <Space direction={isMobileOnly ? "vertical" : "horizontal"} size={isMobileOnly ? "small" : "large"}>
                            <Form.Item name="date"
                                       label={<FormattedMessage id="from_date"/>}
                                       className={"dentobook-form-item"}
                                       initialValue={date}
                            >
                                <Select
                                    style={{width: 230}}
                                    value={date}
                                    size={"large"}
                                    className="icon-select"
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={e => {
                                        setDate(e);

                                        let filteredTreatmentsArray;
                                        if (e === 'all') {
                                            filteredTreatmentsArray = treatments;
                                        } else {
                                            filteredTreatmentsArray = treatments.filter(treatment => formatDate(treatment.date!) === e);
                                        }
                                        if (nzisStatus === 'sent') {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .filter(treatment => treatment.isSentToNzis);
                                        } else if (nzisStatus === 'unsent') {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .filter(treatment => !treatment.isSentToNzis);
                                        } else {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .sort((treatmentOne, treatmentTwo) => {
                                                    if (treatmentOne.isSentToNzis === treatmentTwo.isSentToNzis) {
                                                        return 0;
                                                    } else if (treatmentOne.isSentToNzis) {
                                                        return 1;
                                                    }

                                                    return -1;
                                                });
                                        }

                                        setFilteredTreatments(filteredTreatmentsArray);
                                    }}
                                >
                                    <Select.Option key="allDates" value="all">
                                        <FormattedMessage id="all"/>
                                    </Select.Option>
                                    {dates?.map(displayDate => (
                                        <Select.Option key={displayDate} value={displayDate}>
                                            {displayDate}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item name="status"
                                       label={<FormattedMessage id="status"/>}
                                       className={"dentobook-form-item"}
                                       initialValue={nzisStatus}
                            >
                                <Select
                                    style={{width: 230}}
                                    value={nzisStatus}
                                    size={"large"}
                                    className="icon-select"
                                    suffixIcon={<CaretDownOutlined/>}
                                    onChange={e => {
                                        setNzisStatus(e);

                                        let filteredTreatmentsArray;

                                        if (date === 'all') {
                                            filteredTreatmentsArray = treatments;
                                        } else {
                                            filteredTreatmentsArray = treatments.filter(treatment => formatDate(treatment.date!) === date);
                                        }

                                        if (e === 'sent') {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .filter(treatment => treatment.isSentToNzis);
                                        } else if (e === 'unsent') {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .filter(treatment => !treatment.isSentToNzis);
                                        } else {
                                            filteredTreatmentsArray = filteredTreatmentsArray
                                                .sort((treatmentOne, treatmentTwo) => treatmentOne.isSentToNzis === treatmentTwo.isSentToNzis ? 0 : treatmentOne.isSentToNzis ? 1 : -1);
                                        }

                                        setFilteredTreatments(filteredTreatmentsArray);
                                    }}
                                >
                                    <Select.Option value="sent">
                                        <FormattedMessage id="sent"/>
                                    </Select.Option>
                                    <Select.Option value="unsent">
                                        <FormattedMessage id="unsent"/>
                                    </Select.Option>
                                    <Select.Option value="all">
                                        <FormattedMessage id="all"/>
                                    </Select.Option>
                                </Select>
                            </Form.Item>
                        </Space>
                    </Form>
                    <div className="dentobook-content dentobook-scrollable-table" style={{marginTop: -20, marginBottom: 150}}>
                        <Table
                            rowSelection={rowSelection}
                            className={"teeth-table pt-2"}
                            columns={tableColumns}
                            dataSource={data}
                            pagination={false}/>
                    </div>

                    <FormButtons>
                        <Button
                            className={"dentobook-btn-secondary modal-btn-md font-16 font-weight-500"}
                            type="default"
                            htmlType="button"
                            loading={isPdfDownloading}
                            disabled={isPdfDownloading}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAmbulatorySheet();
                            }}>
                            {<FormattedMessage id="ambulatory_sheet"/>}
                        </Button>
                        <Button
                            className={"dentobook-btn-primary modal-btn-lg font-16 font-weight-500"}
                            style={isMobileOnly ? {marginRight: -45, width: '44%'} : {}}
                            htmlType="button"
                            type="primary"
                            disabled={isNzisSubmitting}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleNZISMultipleItems();
                            }}>
                            {<FormattedMessage id="send_to_nzis"/>}
                        </Button>
                    </FormButtons>
                </div>
            </>
        );
    }
;

export default PatientNzis;